import { useCallback, useMemo } from 'react';
import { formatRoute, queueActs, queueIsEmpty, type QueueState } from '@visious/core';
import { useWatchProgressLookup } from '../history/useWatchProgress';
import { watchPlaylistPageOf } from '../screens/watch/model';
import type {
  WatchListLoad,
  WatchListMove,
  WatchListOrder,
  WatchPlaylistBinding,
} from '../screens/watch/useWatchPlaylist';
import { useCopy } from '../strings/useCopy';
import { queuePanelNeighbours, queuePositionText, toQueuePanelRows } from './model';
import { moveInQueue, removeFromQueue, reverseQueue, useQueue } from './store';

/**
 * The queue as the watch page's side panel — the **only** binding the panel is fed, because the queue is
 * the only list it draws. A `list` in the address makes the queue a window on that playlist
 * (`core/queue/model.ts`'s `QueueLink`), so the rows here are that playlist's rows and there is no
 * second panel to pick between.
 *
 * What the two lists still differ in is what stands behind them, and that arrives as
 * {@link WatchListLoad} rather than being decided here:
 *
 * - **A list of the reader's own loads nothing**, so the load answers `loading`, `loadingMore`,
 *   `hasMore` and `hasEarlier` flatly. The rows are the whole list from the first render, which is also
 *   why the panel's pager is the only thing that ever pages it.
 * - **A removal is a different act on each.** On a linked queue it is the playlist's own removal, which a
 *   server can refuse and which shows an in-flight face; on an unlinked one it is a synchronous edit of
 *   an array. `queueActs` says whether it may be asked for at all — one tested place for the question
 *   rather than this file restating the rule.
 * - **Reverse is a state on one and an act on the other.** A playlist can only be flipped in the display
 *   (there is no reorder endpoint), so the choice is remembered per playlist and the load carries it; a
 *   queue is ours, so `queueReverse` really moves the rows and there is nothing left to remember.
 * - **Only one of them leads anywhere.** A playlist's heading is a link to its page; a queue nobody else
 *   can read has no page of its own, so the panel draws its name as plain text.
 */
export function useQueuePanel(load: WatchListLoad): WatchPlaylistBinding | null {
  const state = useQueue();
  const words = useCopy().text.watch.queue;
  const progress = useWatchProgressLookup();

  const rows = useMemo(() => toQueuePanelRows({ state, progress }), [state, progress]);
  const neighbours = useMemo(() => queuePanelNeighbours(state, rows), [state, rows]);

  /**
   * Which playlist this panel is about, from the link if the queue has one and from the address until it
   * does. The address is what covers the render before the first page lands: the panel is drawn from that
   * moment, so the column does not reshape itself around a section that is one request away, and its
   * heading has to lead to the playlist page already.
   */
  const playlistId = state.link?.playlistId ?? load.playlistId;
  const linked = playlistId !== null;

  const removeEntry = useCallback(
    (key: string) => {
      // A linked row's removal is the playlist's, and the loader is what turns the row's queue key into
      // the key the endpoint accepts. An unlinked one is an edit of this tab's own array.
      if (state.link !== null) load.removeEntry(key);
      else removeFromQueue(key);
    },
    [load, state.link],
  );

  return useMemo(() => {
    // No list at all is no panel. A queue that exists but is playing nothing still draws one: the reader
    // has a list, and the panel is where they press a row to start it again. So does an address naming a
    // playlist whose first page has not landed, which is what keeps the way back to a hidden panel on
    // screen for a list this tab has not read.
    if (queueIsEmpty(state) && !linked) return null;
    return {
      rows,
      title: linked ? (state.link?.title ?? load.title) : words.title,
      href: playlistId === null ? null : formatRoute({ kind: 'playlist', playlistId }),
      positionText: queuePositionText(state),
      loading: load.loading,
      loadingMore: load.loadingMore,
      problem: load.problem,
      entryProblem: load.entryProblem,
      canRemove: queueActs(state).remove,
      removeEntry,
      removingKeys: load.removingKeys,
      move: queueMoves(state),
      next: neighbours.next,
      previous: neighbours.previous,
      order: linked ? load.order : QUEUE_ORDER,
      initialPage: watchPlaylistPageOf(rows),
      hasMore: load.hasMore,
      loadMore: load.loadMore,
      hasEarlier: load.hasEarlier,
      loadEarlier: load.loadEarlier,
    };
  }, [linked, load, neighbours, playlistId, removeEntry, rows, state, words.title]);
}

/**
 * The order switch as a real reorder. A module constant rather than a memo because it closes over
 * nothing: the store reads the live queue itself at the moment of the press, which is the rule every
 * act on a queue keeps (`queue/store.ts`).
 */
const QUEUE_ORDER: WatchListOrder = { kind: 'reorder', reverse: reverseQueue };

/**
 * The rows' arrows, offered where the rows can really change places (`queueActs`, which says no on a
 * linked queue) and where there is somewhere to move to. The ends are named rather than left out, so
 * the first row's up and the last row's down are drawn disabled and the pair keeps its place.
 */
function queueMoves(state: QueueState): WatchListMove | null {
  const first = state.entries[0];
  const last = state.entries.at(-1);
  if (!queueActs(state).reorder || first === undefined || last === undefined || first === last) return null;
  return { act: moveInQueue, firstKey: first.key, lastKey: last.key };
}
